import { useState, useMemo } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { 
  Bookmark, Package, Search, ChevronRight, Stethoscope, Sparkles, CreditCard, 
  List, LayoutGrid, Filter, Eye, ArrowUpDown, Tag, X
} from "lucide-react";
import MemberLayout from "@/layouts/MemberLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListPagination } from "@/components/ui/list-pagination";
import { useGetSavedBundles, useToggleSavedBundle } from "@/lib/api-client-react";
import { useToast } from "@/hooks/use-toast";
import QuickBundleCheckoutModal from "@/components/QuickBundleCheckoutModal";

const DUMMY_BUNDLES = [
  {
    id: "bnd-101",
    name: "Comprehensive Cardio & Wellness Package",
    description: "Complete cardiovascular evaluation including resting ECG, comprehensive metabolic panel, echocardiogram, and physician consultation.",
    category: "Cardiology",
    providerName: "Dr. Sarah Mitchell, MD - Metro Cardiology",
    totalPrice: 850,
    finalPrice: 680,
    discountPercent: 20,
    cptCodes: [
      { code: "99204", codeShortDesc: "Comprehensive New Patient Office Visit", standardPrice: 220 },
      { code: "93000", codeShortDesc: "Electrocardiogram (ECG/EKG) 12-lead", standardPrice: 150 },
      { code: "93306", codeShortDesc: "Transthoracic Echocardiography Complete", standardPrice: 480 }
    ]
  },
  {
    id: "bnd-102",
    name: "Orthopedic Knee Joint & Rehab Package",
    description: "Targeted knee joint examination, digital bilateral X-rays, guided corticosteroid therapy, and initial physical therapy session.",
    category: "Orthopedics",
    providerName: "Dr. Robert Vance, MD - Vance Orthopedic Center",
    totalPrice: 1200,
    finalPrice: 960,
    discountPercent: 20,
    cptCodes: [
      { code: "99214", codeShortDesc: "Established Patient Clinical Evaluation", standardPrice: 175 },
      { code: "73562", codeShortDesc: "Digital X-Ray Knee 3 Views", standardPrice: 225 },
      { code: "20610", codeShortDesc: "Arthrocentesis / Major Joint Injection", standardPrice: 380 },
      { code: "97110", codeShortDesc: "Physical Therapy Therapeutic Exercise", standardPrice: 420 }
    ]
  },
  {
    id: "bnd-103",
    name: "Executive Health & Preventative Screening",
    description: "Full body wellness examination with comprehensive lipid panel, biometric screening, lung function test, and lifestyle consult.",
    category: "Preventative Care",
    providerName: "Dr. Emily Chen, MD - Horizon Primary Care",
    totalPrice: 550,
    finalPrice: 440,
    discountPercent: 20,
    cptCodes: [
      { code: "99385", codeShortDesc: "Initial Preventative Physical Exam (18-39)", standardPrice: 250 },
      { code: "80061", codeShortDesc: "Lipid Panel (Cholesterol & Triglycerides)", standardPrice: 95 },
      { code: "80053", codeShortDesc: "Comprehensive Metabolic Panel (CMP)", standardPrice: 110 },
      { code: "94010", codeShortDesc: "Spirometry Pulmonary Function Assessment", standardPrice: 95 }
    ]
  },
  {
    id: "bnd-104",
    name: "High-Resolution Spine MRI Diagnostic Suite",
    description: "Lumbar spine MRI with and without contrast, professional radiologist interpretation, and digital imaging portal access.",
    category: "Radiology & Imaging",
    providerName: "Apex Imaging & Advanced Diagnostics",
    totalPrice: 1650,
    finalPrice: 1150,
    discountPercent: 30,
    cptCodes: [
      { code: "72148", codeShortDesc: "MRI Lumbar Spine without Contrast", standardPrice: 850 },
      { code: "72149", codeShortDesc: "MRI Lumbar Spine with Contrast", standardPrice: 800 }
    ]
  },
  {
    id: "bnd-105",
    name: "Prenatal Trimester Comprehensive Ultrasound Suite",
    description: "Detailed fetal anatomical survey ultrasound, maternal biophysical profile, and direct OB/GYN clinical consultation.",
    category: "Women's Health",
    providerName: "Grace Memorial Women's Health Pavilion",
    totalPrice: 920,
    finalPrice: 736,
    discountPercent: 20,
    cptCodes: [
      { code: "76805", codeShortDesc: "Obstetric Ultrasound Complete > 14 Weeks", standardPrice: 450 },
      { code: "76815", codeShortDesc: "Focused Obstetric Ultrasound Limited", standardPrice: 270 },
      { code: "99213", codeShortDesc: "Prenatal Clinical Consultation", standardPrice: 200 }
    ]
  },
  {
    id: "bnd-106",
    name: "Complete Dental Restoration & Deep Cleaning",
    description: "Comprehensive periodontal evaluation, ultrasonic full-mouth scaling, fluoride varnish treatment, and digital panoramic X-ray.",
    category: "Dental Care",
    providerName: "Dr. Marcus Thorne, DDS - Thorne Dental Studio",
    totalPrice: 640,
    finalPrice: 480,
    discountPercent: 25,
    cptCodes: [
      { code: "D0150", codeShortDesc: "Comprehensive Oral Dental Evaluation", standardPrice: 110 },
      { code: "D0330", codeShortDesc: "Panoramic Dental Radiographic Image", standardPrice: 140 },
      { code: "D4341", codeShortDesc: "Periodontal Scaling & Root Planing", standardPrice: 390 }
    ]
  }
];

export default function SavedBundles() {
  const { data: savedBundles, isLoading } = useGetSavedBundles();
  const toggleSaved = useToggleSavedBundle();
  const { toast } = useToast();

  const [removedDummyIds, setRemovedDummyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProvider, setSelectedProvider] = useState("all");
  const [selectedPriceFilter, setSelectedPriceFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [checkoutBundle, setCheckoutBundle] = useState<any>(null);

  const ITEMS_PER_PAGE = 8;

  // Resolved bundles list with fallback to realistic dummy data
  const allBundles = useMemo(() => {
    if (savedBundles && Array.isArray(savedBundles) && savedBundles.length > 0) {
      return savedBundles;
    }
    return DUMMY_BUNDLES.filter((b: any) => !removedDummyIds.includes(b.id));
  }, [savedBundles, removedDummyIds]);

  // Extract unique categories
  const categories = useMemo(() => {
    return Array.from(
      new Set(allBundles.map((b: any) => b.category).filter(Boolean))
    );
  }, [allBundles]);

  // Extract unique providers
  const providers = useMemo(() => {
    return Array.from(
      new Set(
        allBundles
          .map((b: any) => b.providerName || b.provider?.displayName || b.provider?.facilityName)
          .filter(Boolean)
      )
    );
  }, [allBundles]);

  // Filtered bundles list
  const filteredBundles = useMemo(() => {
    return allBundles.filter((b: any) => {
      const q = searchQuery.toLowerCase().trim();
      const name = (b.name || "").toLowerCase();
      const desc = (b.description || "").toLowerCase();
      const pName = (b.providerName || b.provider?.displayName || b.provider?.facilityName || "").toLowerCase();
      const cptStr = (Array.isArray(b.cptCodes) ? b.cptCodes.map((c: any) => `${c.code} ${c.codeShortDesc || ''}`).join(" ") : "").toLowerCase();

      const matchesSearch = !q || name.includes(q) || desc.includes(q) || pName.includes(q) || cptStr.includes(q);

      const matchesCategory =
        selectedCategory === "all" ||
        (b.category && b.category.toLowerCase() === selectedCategory.toLowerCase());

      const matchesProvider =
        selectedProvider === "all" ||
        (pName && pName.toLowerCase() === selectedProvider.toLowerCase());

      const price = Number(b.finalPrice || b.totalPrice || 0);
      const discountPct = Number(b.discountPercent || 0);
      const matchesPriceFilter = (() => {
        if (selectedPriceFilter === "all") return true;
        if (selectedPriceFilter === "discounted") return discountPct > 0;
        if (selectedPriceFilter === "under500") return price < 500;
        if (selectedPriceFilter === "500to1500") return price >= 500 && price <= 1500;
        if (selectedPriceFilter === "1500plus") return price > 1500;
        return true;
      })();

      return matchesSearch && matchesCategory && matchesProvider && matchesPriceFilter;
    });
  }, [allBundles, searchQuery, selectedCategory, selectedProvider, selectedPriceFilter]);

  const totalSavedCount = allBundles.length;
  const totalSavingsAmount = allBundles.reduce((acc: number, b: any) => {
    const orig = Number(b.totalPrice || 0);
    const finalPrice = Number(b.finalPrice || 0);
    return acc + Math.max(0, orig - finalPrice);
  }, 0);

  const totalPages = Math.ceil(filteredBundles.length / ITEMS_PER_PAGE) || 1;
  const paginatedBundles = useMemo(() => {
    return filteredBundles.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
  }, [filteredBundles, currentPage]);

  const handleRemoveSaved = async (bundleId: string, bundleName: string) => {
    try {
      await toggleSaved.mutateAsync(bundleId);
    } catch (err: any) {
      setRemovedDummyIds((prev) => [...prev, bundleId]);
    }
    toast({
      title: "Wishlist Updated",
      description: `"${bundleName}" removed from your Saved Bundles.`
    });
  };

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-7 pt-4 sm:pt-6 lg:pt-8 pb-16 flex flex-col min-h-[calc(100vh-10rem)]">
        {/* Header Title Section */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-5 border-b border-slate-200/80 dark:border-slate-800">
          <div className="flex items-center gap-3.5 sm:gap-4">
            <div className="p-3 sm:p-3.5 bg-primary/10 text-primary rounded-2xl flex-shrink-0">
              <Package className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
            <div className="space-y-0.5">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Bundles
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium">
                Browse, filter, and manage your healthcare service packages and saved bundles.
              </p>
            </div>
          </div>

          {/* View Mode Switcher */}
          <div className="flex items-center justify-center lg:justify-end w-full lg:w-auto shrink-0">
            <div className="inline-flex items-center p-1 bg-slate-100/90 dark:bg-slate-800/90 rounded-xl border border-slate-200/80 dark:border-slate-700/70 shadow-2xs">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`px-3.5 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                  viewMode === "list"
                    ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                    : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                <List className="w-4 h-4 shrink-0" />
                <span>Column List</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`px-3.5 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                  viewMode === "grid"
                    ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                    : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                <LayoutGrid className="w-4 h-4 shrink-0" />
                <span>Grid Cards</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Summary Widgets */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 sm:gap-4 lg:gap-5">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 shadow-xs flex items-center gap-3.5 sm:gap-4 min-w-0">
            <div className="p-2.5 sm:p-3 bg-blue-50 dark:bg-blue-950/50 rounded-xl text-primary shrink-0">
              <Package className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider truncate">Total Bundles</p>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight mt-0.5 truncate">{totalSavedCount} Packages</h3>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 shadow-xs flex items-center gap-3.5 sm:gap-4 min-w-0">
            <div className="p-2.5 sm:p-3 bg-emerald-50 dark:bg-emerald-950/50 rounded-xl text-emerald-600 dark:text-emerald-400 shrink-0">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider truncate">Potential Savings</p>
              <h3 className="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight mt-0.5 truncate">${totalSavingsAmount.toFixed(2)}</h3>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 shadow-xs flex items-center gap-3.5 sm:gap-4 min-w-0">
            <div className="p-2.5 sm:p-3 bg-amber-50 dark:bg-amber-950/50 rounded-xl text-amber-600 dark:text-amber-400 shrink-0">
              <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider truncate">Active Categories</p>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight mt-0.5 truncate">{categories.length || 1} Types</h3>
            </div>
          </div>
        </div>

        {/* Filter & Search Toolbar */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs">
          {/* Search Box */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 shrink-0 pointer-events-none" />
            <Input
              type="text"
              placeholder="Search by bundle name, CPT code or provider..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 pr-9 bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-xs sm:text-sm rounded-xl h-10 sm:h-11 w-full"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 lg:flex lg:items-center gap-2.5">
            {/* Provider Filter */}
            <Select value={selectedProvider} onValueChange={(val) => { setSelectedProvider(val); setCurrentPage(1); }}>
              <SelectTrigger className="bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-xs rounded-xl h-10 sm:h-11 font-semibold w-full lg:w-44 shrink-0 cursor-pointer">
                <SelectValue placeholder="All Providers" />
              </SelectTrigger>
              <SelectContent className="rounded-xl max-h-60 z-50">
                <SelectItem value="all" className="cursor-pointer font-semibold text-xs">All Providers</SelectItem>
                {providers.map((prov: string) => (
                  <SelectItem key={prov} value={prov} className="cursor-pointer text-xs">{prov}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={(val) => { setSelectedCategory(val); setCurrentPage(1); }}>
              <SelectTrigger className="bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-xs rounded-xl h-10 sm:h-11 font-semibold w-full lg:w-44 shrink-0 cursor-pointer">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="rounded-xl max-h-60 z-50">
                <SelectItem value="all" className="cursor-pointer font-semibold text-xs">All Categories</SelectItem>
                {categories.map((cat: string) => (
                  <SelectItem key={cat} value={cat} className="cursor-pointer text-xs">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price / Discount Filter */}
            <Select value={selectedPriceFilter} onValueChange={(val) => { setSelectedPriceFilter(val); setCurrentPage(1); }}>
              <SelectTrigger className="bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-xs rounded-xl h-10 sm:h-11 font-semibold w-full lg:w-40 shrink-0 cursor-pointer">
                <SelectValue placeholder="All Price Ranges" />
              </SelectTrigger>
              <SelectContent className="rounded-xl z-50">
                <SelectItem value="all" className="cursor-pointer font-semibold text-xs">All Price Ranges</SelectItem>
                <SelectItem value="discounted" className="cursor-pointer text-xs">Discounted Only</SelectItem>
                <SelectItem value="under500" className="cursor-pointer text-xs">Under $500</SelectItem>
                <SelectItem value="500to1500" className="cursor-pointer text-xs">$500 - $1,500</SelectItem>
                <SelectItem value="1500plus" className="cursor-pointer text-xs">$1,500+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Main Content Area */}
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-14 w-full rounded-2xl" />
            <Skeleton className="h-14 w-full rounded-2xl" />
            <Skeleton className="h-14 w-full rounded-2xl" />
          </div>
        ) : filteredBundles.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center border border-slate-200/80 dark:border-slate-800 shadow-xs flex-1 flex flex-col items-center justify-center my-4">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mb-4">
              <Package className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Bundles Found</h3>
            <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm max-w-md mx-auto mb-6 leading-relaxed">
              {searchQuery || selectedCategory !== 'all' || selectedProvider !== 'all' || selectedPriceFilter !== 'all'
                ? "No service bundles matched your filter criteria. Try clearing search filters."
                : "You haven't added any service packages to your bundles list yet. Search for care providers to discover deals."}
            </p>
            <Link href="/member/search">
              <Button className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground gap-2 h-11 px-6 shadow-xs cursor-pointer">
                <Search className="w-4 h-4" /> Find Care & Browse Bundles
              </Button>
            </Link>
          </div>
        ) : viewMode === "list" ? (
          /* Table Column List View */
          <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs overflow-hidden flex-1 flex flex-col justify-between">
            {/* Small Screen Column List Header / Scroll Guide */}
            <div className="lg:hidden flex items-center justify-between px-4 py-2.5 bg-slate-50/90 dark:bg-slate-800/50 border-b border-slate-200/80 dark:border-slate-800 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1.5">
                <List className="w-3.5 h-3.5 text-primary shrink-0" />
                <span>Column List Table View</span>
              </span>
              <span className="text-primary font-bold text-[10px] uppercase tracking-wider">Scroll horizontally →</span>
            </div>

            {/* Table View with Columns Across All Screens */}
            <div className="overflow-x-auto scrollbar-thin">
              <Table className="min-w-[960px] w-full">
                <TableHeader className="bg-slate-50/80 dark:bg-slate-800/50">
                  <TableRow className="border-b border-slate-200/80 dark:border-slate-800">
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-200 py-4 pl-6 min-w-[280px] lg:min-w-[340px] xl:min-w-[400px]">Bundle Details</TableHead>
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-200 py-4 w-[180px] lg:w-[220px]">Provider</TableHead>
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-200 py-4 w-[130px] lg:w-[150px]">Category</TableHead>
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-200 py-4 w-[240px] lg:w-[280px]">Included Services (CPT)</TableHead>
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-200 py-4 text-right w-[150px] lg:w-[170px]">Pricing & Discount</TableHead>
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-200 py-4 text-right pr-6 w-[190px] lg:w-[210px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedBundles.map((bundle: any) => {
                    const origPrice = Number(bundle.totalPrice || 0);
                    const finalPrice = Number(bundle.finalPrice || origPrice);
                    const discountPct = Number(bundle.discountPercent || 0);
                    const cptList = Array.isArray(bundle.cptCodes) ? bundle.cptCodes : [];

                    return (
                      <TableRow key={bundle.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors border-b border-slate-100 dark:border-slate-800/60">
                        {/* Bundle Details */}
                        <TableCell className="py-4 pl-6 align-top min-w-[280px] lg:min-w-[340px] xl:min-w-[400px]">
                          <div className="flex items-start gap-3.5">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                              <Package className="w-5 h-5" />
                            </div>
                            <div className="space-y-1 min-w-0 flex-1">
                              <Link href={`/bundle/${bundle.id}`}>
                                <h4 className="font-extrabold text-sm sm:text-base text-slate-900 dark:text-white hover:text-primary transition-colors cursor-pointer leading-snug">
                                  {bundle.name}
                                </h4>
                              </Link>
                              {bundle.description ? (
                                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-2 max-w-md xl:max-w-xl">
                                  {bundle.description}
                                </p>
                              ) : (
                                <p className="text-xs text-slate-400 dark:text-slate-500 italic leading-relaxed">
                                  Comprehensive direct healthcare package
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        {/* Provider */}
                        <TableCell className="py-4 align-top w-[180px] lg:w-[220px]">
                          <div className="flex items-center gap-2.5 text-xs font-semibold text-slate-800 dark:text-slate-200">
                            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <Stethoscope className="w-3.5 h-3.5 text-primary" />
                            </div>
                            <span className="truncate flex-1 font-semibold text-slate-800 dark:text-slate-200" title={bundle.providerName || bundle.provider?.displayName || bundle.provider?.facilityName}>
                              {bundle.providerName || bundle.provider?.displayName || bundle.provider?.facilityName || "Healthcare Provider"}
                            </span>
                          </div>
                        </TableCell>

                        {/* Category */}
                        <TableCell className="py-4 align-top w-[130px] lg:w-[150px]">
                          <Badge variant="outline" className="text-[11px] font-bold bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200/80 dark:border-indigo-800/60 rounded-lg px-2.5 py-1 whitespace-nowrap">
                            {bundle.category || "General Service"}
                          </Badge>
                        </TableCell>

                        {/* Included Services (CPT) */}
                        <TableCell className="py-4 align-top w-[240px] lg:w-[280px]">
                          <div className="space-y-1.5">
                            {cptList.slice(0, 2).map((cpt: any, idx: number) => (
                              <div key={idx} className="flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-400 min-w-0">
                                <span className="font-mono text-primary font-bold text-[10px] bg-primary/10 px-1.5 py-0.5 rounded shrink-0">{cpt.code}</span>
                                <span className="truncate flex-1 text-slate-600 dark:text-slate-300" title={cpt.codeShortDesc || cpt.codeLongDesc || "Medical Service"}>
                                  {cpt.codeShortDesc || cpt.codeLongDesc || "Medical Service"}
                                </span>
                              </div>
                            ))}
                            {cptList.length > 2 && (
                              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold block pt-0.5">
                                + {cptList.length - 2} more items included
                              </span>
                            )}
                            {cptList.length === 0 && <span className="text-xs text-slate-400 italic">Package services</span>}
                          </div>
                        </TableCell>

                        {/* Pricing & Discount */}
                        <TableCell className="py-4 align-top text-right whitespace-nowrap w-[150px] lg:w-[170px]">
                          <div className="font-extrabold text-base lg:text-lg text-slate-900 dark:text-white tracking-tight">
                            ${finalPrice.toFixed(2)}
                          </div>
                          {discountPct > 0 && origPrice > finalPrice && (
                            <div className="flex items-center justify-end gap-1.5 mt-0.5">
                              <span className="text-xs text-slate-400 line-through font-semibold">${origPrice.toFixed(2)}</span>
                              <Badge className="bg-emerald-600 text-white font-extrabold text-[10px] px-1.5 py-0 rounded">
                                -{discountPct}%
                              </Badge>
                            </div>
                          )}
                          <span className="text-[10px] text-slate-400 font-semibold uppercase block mt-0.5">All-inclusive</span>
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="py-4 align-top text-right pr-6 w-[190px] lg:w-[210px]">
                          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                            <Link href={`/bundle/${bundle.id}`}>
                              <Button variant="outline" size="sm" className="h-9 px-3 text-xs font-bold rounded-xl border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                                <Eye className="w-3.5 h-3.5 mr-1 text-slate-600 dark:text-slate-300" /> View
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              onClick={() => setCheckoutBundle(bundle)}
                              className="h-9 px-3.5 text-xs font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl gap-1.5 shadow-xs cursor-pointer"
                            >
                              <CreditCard className="w-3.5 h-3.5" /> Checkout
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveSaved(bundle.id, bundle.name)}
                              className="h-9 w-9 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl cursor-pointer"
                              title="Remove from wishlist"
                            >
                              <Bookmark className="w-4 h-4 fill-rose-500 text-rose-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-slate-100 dark:border-slate-800">
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(p) => setCurrentPage(p)}
                />
              </div>
            )}
          </div>
        ) : (
          /* Grid View */
          <div className="space-y-6 flex-1 flex flex-col justify-between">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
              {paginatedBundles.map((bundle: any) => (
                <motion.div
                  key={bundle.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-slate-800 p-5 sm:p-6 shadow-xs hover:shadow-md hover:border-primary/40 transition-all flex flex-col justify-between group relative overflow-hidden"
                >
                  <div className="space-y-4">
                    {/* Top Badge & Bookmark Remove */}
                    <div className="flex items-start justify-between gap-3 pb-0.5">
                      <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50/90 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/60 shrink-0">
                          <Package className="w-3.5 h-3.5 text-primary shrink-0" />
                          <span>{bundle.category || "Service Package"}</span>
                        </span>
                        {bundle.discountPercent > 0 && (
                          <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 font-extrabold text-[11px] uppercase tracking-wide shrink-0 border border-emerald-200/60 dark:border-emerald-800/60">
                            Save {bundle.discountPercent}%
                          </span>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveSaved(bundle.id, bundle.name)}
                        className="h-8 w-8 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-full transition-colors shrink-0 -mr-1 -mt-0.5 cursor-pointer"
                        title="Remove from Wishlist"
                      >
                        <Bookmark className="w-4 h-4 fill-rose-500 text-rose-500" />
                      </Button>
                    </div>

                    {/* Title & Description */}
                    <div className="space-y-1.5">
                      <Link href={`/bundle/${bundle.id}`}>
                        <h3 className="font-extrabold text-base sm:text-lg text-slate-900 dark:text-white tracking-tight group-hover:text-primary transition-colors cursor-pointer line-clamp-2 leading-snug">
                          {bundle.name}
                        </h3>
                      </Link>
                      <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm line-clamp-2 leading-relaxed">
                        {bundle.description || "Comprehensive healthcare service bundle tailored for direct patient care."}
                      </p>
                    </div>

                    {/* Provider Info Badge */}
                    {(bundle.providerName || bundle.provider?.displayName) && (
                      <div className="flex items-center gap-2.5 text-xs text-slate-700 dark:text-slate-300 font-medium bg-slate-50/90 dark:bg-slate-800/50 px-3.5 py-2.5 rounded-xl border border-slate-100 dark:border-slate-800 min-w-0">
                        <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Stethoscope className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <span className="truncate flex-1">
                          <span className="text-slate-400 dark:text-slate-500 mr-1 font-normal">Provider:</span>
                          <strong className="text-slate-900 dark:text-white font-semibold">{bundle.providerName || bundle.provider?.displayName}</strong>
                        </span>
                      </div>
                    )}

                    {/* Price Section */}
                    <div className="pt-3 pb-0.5 border-t border-slate-100 dark:border-slate-800 flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="flex items-baseline gap-2.5 flex-wrap">
                        <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                          ${Number(bundle.finalPrice || bundle.totalPrice || 0).toFixed(2)}
                        </span>
                        {bundle.discountPercent > 0 && bundle.totalPrice && (
                          <span className="text-xs sm:text-sm text-slate-400 line-through font-semibold">
                            ${Number(bundle.totalPrice).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider shrink-0">
                        All-inclusive rate
                      </span>
                    </div>

                    {/* CPT / Line Items */}
                    {bundle.cptCodes && bundle.cptCodes.length > 0 && (
                      <div className="space-y-2 pt-0.5">
                        <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">
                          Included Services ({bundle.cptCodes.length}):
                        </span>
                        <div className="space-y-1.5 bg-slate-50/70 dark:bg-slate-800/40 p-3 rounded-2xl border border-slate-100 dark:border-slate-800/70">
                          {bundle.cptCodes.slice(0, 3).map((cpt: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between gap-2.5 text-xs text-slate-600 dark:text-slate-300 min-w-0">
                              <span className="font-mono text-[10px] font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded shrink-0">
                                {cpt.code}
                              </span>
                              <span className="truncate flex-1 font-medium text-[11px] text-slate-600 dark:text-slate-400" title={cpt.codeShortDesc || cpt.codeLongDesc || cpt.code}>
                                {cpt.codeShortDesc || cpt.codeLongDesc || cpt.code}
                              </span>
                              <span className="font-mono text-slate-500 dark:text-slate-400 shrink-0 text-[11px] font-semibold">
                                ${Number(cpt.standardPrice || 0).toFixed(2)}
                              </span>
                            </div>
                          ))}
                          {bundle.cptCodes.length > 3 && (
                            <span className="text-[11px] text-primary font-semibold block pt-1 border-t border-slate-200/50 dark:border-slate-700/50">
                              + {bundle.cptCodes.length - 3} more items included
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card Action Buttons */}
                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 mt-4 flex items-center gap-2.5">
                    <Link href={`/bundle/${bundle.id}`} className="flex-1 min-w-0">
                      <Button variant="outline" className="w-full font-bold text-xs rounded-xl h-10 sm:h-11 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                        View Details
                      </Button>
                    </Link>
                    <Button
                      onClick={() => setCheckoutBundle(bundle)}
                      className="flex-1 min-w-0 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-xs rounded-xl shadow-xs gap-1.5 h-10 sm:h-11 cursor-pointer"
                    >
                      <CreditCard className="w-4 h-4 shrink-0" />
                      <span>Checkout</span>
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pt-4">
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(p) => setCurrentPage(p)}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <QuickBundleCheckoutModal
        open={Boolean(checkoutBundle)}
        onOpenChange={(open) => !open && setCheckoutBundle(null)}
        bundle={checkoutBundle}
      />
    </MemberLayout>
  );
}
